import { Status } from '../../common/enums/status';
import { UserSessionResponse } from '../../services/ocpp/types';
import { SessionType } from '../../components/common/Card/UserSessionCard/types';

/** What a fresh REST snapshot says about the charge this screen is about. */
export interface ScreenSession {
  /** The connector this screen's own charge is on, or null when it has none. */
  activeConnectorId: number | null;
  activeSession: SessionType | null;
  progressPercent: number;
  /** Every connector at this station the account is charging on. */
  myConnectorIds: number[];
}

/**
 * Which charge, if any, the station screen is about.
 *
 * The screen is addressed by station *and* connector
 * (`station/:id/:connectorId?`), so a charge is this screen's own only when it
 * runs on the connector named in the URL. Matching on the station alone pinned
 * the screen to whichever connector was already charging: on a two-connector
 * station the driver was shown their own charge's view whichever connector
 * they picked, and could not start a second charge on the free one at all —
 * even though the backend's start guard is per connector and would have taken
 * it.
 *
 * With no connector in the URL the screen means "this station". On a one-bay
 * station that is the same thing as naming the bay, so a charge running there
 * is this screen's and still wins — a bare QR scan reopens it. On a station
 * with more than one bay it is not: a driver already charging on bay 1 who
 * arrives by the map, the station card or a scan of the station's own QR is as
 * likely to have come for bay 2. Adopting the running charge there put its
 * charging view on the screen, and the picker — the only route to the free bay
 * — never rendered. So the station-wide screen resolves to no charge and the
 * caller offers the picker, which flags the driver's own bay and reopens it in
 * one tap.
 */
export const resolveScreenSession = (
  stationId: string,
  connectorFromUrl: string | undefined,
  lastSession: SessionType | null,
  activeSessions: UserSessionResponse[],
  /** Usable bays at this station — what decides whether a bare URL names one. */
  usableConnectorCount: number,
): ScreenSession => {
  const here = activeSessions.filter((session) => session.stationId === stationId);
  const myConnectorIds = here
    .map((session) => Number(session.connectorId))
    .filter((connectorId) => Number.isFinite(connectorId));

  const wanted = connectorFromUrl ? Number(connectorFromUrl) : null;
  // A bare URL names the station, and stands in for the bay only when the
  // station has just one. Otherwise this screen is about no bay in particular,
  // and no charge is its own.
  const namesABay = wanted !== null || usableConnectorCount <= 1;
  const isThisScreen = (connectorId: string | number | undefined) =>
    namesABay && (wanted === null || Number(connectorId) === wanted);

  const activeProgress = here.find(
    (session) => !!session.connectorId && isThisScreen(session.connectorId),
  );
  const fromLast =
    lastSession?.chargingStatus === Status.CHARGING &&
    isThisScreen(lastSession.connectorId);

  let activeConnectorId: number | null = null;
  let activeSession: SessionType | null = null;
  if (fromLast) {
    activeConnectorId = +lastSession!.connectorId;
    activeSession = lastSession;
  } else if (activeProgress?.connectorId) {
    activeConnectorId = +activeProgress.connectorId;
    activeSession = {
      stationId: activeProgress.stationId!,
      connectorId: activeProgress.connectorId,
      sessionId: Number(activeProgress.sessionId),
      chargingStatus: Status.CHARGING,
      meterValue: activeProgress.meterValue ?? 0,
      meterStart: activeProgress.meterStart ?? 0,
      meterStop: activeProgress.meterStop ?? 0,
    } as SessionType;
  }

  return {
    activeConnectorId,
    activeSession,
    // No charge on this screen's connector reads as no progress, as it did
    // when the endpoint answered with a bare `{ percent: 0 }`.
    progressPercent: activeProgress?.percent ?? 0,
    myConnectorIds,
  };
};
